import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MagnifyingGlass, PaperPlaneTilt, Play } from '@phosphor-icons/react';
import { Slide } from '../Slide';
import { InView } from '../InView';
import { SlideHeadline } from '../SlideHeadline';
import { featuredSnak, shelfSnaks, shelfThumb, stationFeaturedImage, stationFilters, stationPillars, stationScreenshot, stationSource } from '../../../data/station';
import type { ShelfSnak, StationFilter } from '../../../data/station';
export function StationSlide() {
  const [filter, setFilter] = useState<StationFilter>('All');
  const [query, setQuery] = useState('');
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return shelfSnaks.filter(snak => (filter === 'All' || snak.category === filter) && (!q || `${snak.title} ${snak.category}`.toLowerCase().includes(q)));
  }, [filter, query]);
  return <Slide id="station" data-id="element-394" data-mp="3e84p8jwpln3q">
      <div className="grid items-end gap-8 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16" data-id="element-395" data-mp="125bdzyllmmr0">
        <InView data-id="element-396" data-mp="1744p5kdey1o0">
          <SlideHeadline lead="Every Snak on one shelf." follow="Every market picks what works." data-id="element-397" data-mp="174oa79yd1669" />
        </InView>
        <InView delay={0.1} data-id="element-398" data-mp="s5u9hki32ewm">
          <p className="font-body text-[14.5px] leading-[1.7] text-[rgba(1,51,43,0.65)]" data-id="element-399" data-mp="s6m5uxji8gny">
            Snakker Station stores every Snak in one place, so any country can see what is
            working elsewhere — then preview, reuse and localize it instead of starting over.
          </p>
        </InView>
      </div>

      <InView delay={0.16} y={26} className="mt-12" data-id="element-400" data-mp="o73lc2vli7ln">
        <div className="overflow-hidden rounded-[30px] bg-white ring-1 ring-[rgba(1,51,43,0.08)] shadow-[0_50px_90px_-60px_rgba(1,51,43,0.6)]" data-id="element-401" data-mp="9hfi2w3usyva">
          <div className="p-5 sm:p-7" data-id="element-402" data-mp="ed4l59f30xwc">
            {/* station header */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:gap-6" data-id="element-403" data-mp="g7ub7yyrgfg">
              <div className="flex shrink-0 items-center gap-3" data-id="element-404" data-mp="4fzzrbt296gl">
                <span className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-background text-primary" data-id="element-405" data-mp="274gop2aovufr">
                  <Play size={18} weight="fill" aria-hidden="true" data-id="element-406" data-mp="22c6z5ndhkwe0" />
                </span>
                <span className="font-heading text-[26px] leading-none tracking-[-0.02em] text-background" data-id="element-407" data-mp="1xddjboaq8tao">
                  Snakker Station
                </span>
              </div>
              <label className="flex flex-1 items-center gap-2.5 rounded-full bg-[#F4F2EC] px-4 py-2.5 focus-within:ring-2 focus-within:ring-primary" data-id="element-408" data-mp="2q3cgy82zlyqy">
                <MagnifyingGlass size={16} className="text-[rgba(1,51,43,0.45)]" aria-hidden="true" data-id="element-409" data-mp="2l4iz63go3lkz" />
                <span className="sr-only" data-id="element-410" data-mp="2g2trmxsnlq1n">Search Snaks</span>
                <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder="Search — try “congress” or “follow-up”" className="w-full bg-transparent font-body text-[13px] text-background placeholder:text-[rgba(1,51,43,0.4)] focus:outline-none" data-id="element-411" data-mp="2c200lvjwzv4j" />
              </label>
              <span className="shrink-0 font-body text-[12.5px] font-semibold text-[#05a63a]" data-id="element-412" data-mp="3gduzual9km7l">
                Discover. Preview. Reuse. Localize. Activate.
              </span>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2" data-id="element-413" data-mp="3q3oan9dl0xas">
              {stationFilters.map(item => <button key={item} type="button" onClick={() => setFilter(item)} aria-pressed={filter === item} className={['rounded-full px-3.5 py-1.5 font-body text-[12px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary', filter === item ? 'bg-background text-white' : 'bg-[#F4F2EC] text-background hover:bg-[#EAE6DC]'].join(' ')} data-id="element-414" data-mp="3v17o472gh61o">
                  {item}
                </button>)}
              <span className="ml-auto hidden font-body text-[11.5px] text-[rgba(1,51,43,0.45)] md:inline" data-id="element-415" data-mp="1eegh5ns3an2v">
                {shelfSnaks.length + 1} Snaks · curated for global and local teams
              </span>
            </div>

            <div className="mt-6 grid gap-6 lg:grid-cols-[260px_minmax(0,0.8fr)_minmax(0,1.6fr)]" data-id="element-416" data-mp="1odfggfqyt6d7">
              {/* featured preview */}
              <div className="flex items-center justify-center rounded-[22px] bg-[#EFECE4] px-4 py-5" data-id="element-417" data-mp="10kp78eag516m">
                <img src={stationFeaturedImage} alt="The Filtek Snak on a phone: 'Families are funny.' with the family portraits" className="block h-[420px] w-auto mix-blend-multiply" data-id="element-418" data-mp="vlm3cx67d108" />
              </div>

              {/* featured details */}
              <div className="flex flex-col" data-id="element-419" data-mp="cx8mpxtfaaq8">
                <span className="w-fit rounded-full bg-[#E6F7EC] px-3 py-1 font-body text-[11px] font-medium text-[#05753a]" data-id="element-420" data-mp="cwfpbngcsxkr">
                  {featuredSnak.category}
                </span>
                <h3 className="mt-3 font-heading text-[26px] leading-[1.05] tracking-[-0.02em] text-background" data-id="element-421" data-mp="2tccbxkwgmwnl">
                  {featuredSnak.title}
                </h3>
                <p className="mt-1.5 font-body text-[12.5px] text-[rgba(1,51,43,0.55)]" data-id="element-422" data-mp="2jl7vshv1t5r6">
                  {featuredSnak.usedIn}
                </p>
                <span className="mt-6 font-body text-[10px] uppercase tracking-[0.2em] text-[rgba(1,51,43,0.45)]" data-id="element-423" data-mp="37blkbyefcmh0">
                  Helps solve
                </span>
                <p className="mt-1.5 font-heading text-[20px] leading-[1.2] text-background" data-id="element-424" data-mp="2yb5ewje4f59i">
                  {featuredSnak.solves}
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5" data-id="element-425" data-mp="32dtpowmeam94">
                  {featuredSnak.channels.map(channel => <span key={channel} className="rounded-full border border-[rgba(1,51,43,0.18)] px-3 py-1 font-body text-[11.5px] text-background" data-id="element-426" data-mp="1qwk526rj5o68">
                      {channel}
                    </span>)}
                </div>
                <span className="mt-auto inline-flex w-fit items-center gap-2 rounded-full bg-background px-4 py-2.5 pt-2.5 font-body text-[12.5px] font-medium text-white" data-id="element-427" data-mp="3jsw83z9evwb7">
                  <PaperPlaneTilt size={14} className="text-primary" aria-hidden="true" data-id="element-428" data-mp="3aspzorsklmiw" />
                  Request adaptation
                </span>
              </div>

              {/* shelf */}
              <div data-id="element-429" data-mp="30utndwnwclj7">
                <p className="font-heading text-[17px] text-background" data-id="element-430" data-mp="30u0futh45s00">
                  More on the shelf{' '}
                  <span className="text-[rgba(1,51,43,0.4)]" data-id="element-431" data-mp="2vws7fklrp2by">· {visible.length}</span>
                </p>
                <ul className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4" data-id="element-432" data-mp="z25kjof6003y">
                  <AnimatePresence mode="popLayout" data-id="element-433" data-mp="u441h3hqo0sh">
                    {visible.map(snak => <motion.li key={snak.id} layout initial={{
                    opacity: 0,
                    scale: 0.96
                  }} animate={{
                    opacity: 1,
                    scale: 1
                  }} exit={{
                    opacity: 0,
                    scale: 0.96
                  }} transition={{
                    duration: 0.25
                  }} data-id="element-434" data-mp="12m61cg4da6z1">
                        <ShelfThumb snak={snak} data-id="element-435" data-mp="1gelv0qbdosna" />
                        <p className="mt-2 font-heading text-[13px] leading-tight text-background" data-id="element-436" data-mp="jnjyavpai5sh">
                          {snak.title}
                        </p>
                        <p className="mt-0.5 font-body text-[11px] text-[rgba(1,51,43,0.5)]" data-id="element-437" data-mp="sngesa1izycg">
                          {snak.category}
                        </p>
                      </motion.li>)}
                  </AnimatePresence>
                </ul>
                {visible.length === 0 && <p className="mt-6 font-body text-[13px] text-[rgba(1,51,43,0.5)]" data-id="element-438" data-mp="290nel2vx3wqj">
                    No Snaks match that yet — try another filter.
                  </p>}
              </div>
            </div>
          </div>

          {/* pillars */}
          <div className="grid gap-6 bg-background px-5 py-7 sm:px-7 lg:grid-cols-[1fr_1fr_1fr_1.1fr] lg:items-center" data-id="element-439" data-mp="33vahf2fck2hu">
            {stationPillars.map(pillar => <div key={pillar.label} data-id="element-440" data-mp="2z399fsp7oxxm">
                <span className="font-body text-[10px] uppercase tracking-[0.22em] text-primary" data-id="element-441" data-mp="3dt843lhk5r8c">
                  {pillar.label}
                </span>
                <p className="mt-1.5 font-body text-[13px] leading-relaxed text-white/80" data-id="element-442" data-mp="38u3uwuumvti0">
                  {pillar.text}
                </p>
              </div>)}
            <p className="font-heading text-[20px] leading-[1.2] text-white lg:text-right" data-id="element-443" data-mp="3hw4o9gmqxvee">
              One shared platform. Central intelligence. Local empowerment.
            </p>
          </div>
        </div>
      </InView>
    </Slide>;
}
function ShelfThumb({
  snak
}: {
  snak: ShelfSnak;
}) {
  return <div className="relative overflow-hidden rounded-[12px] bg-[#F4F2EC]" style={{
    aspectRatio: `${shelfThumb.width} / ${shelfThumb.height}`
  }} data-id="element-444" data-mp="30b2w0w9zyl2">
      <img src={stationScreenshot} alt={`${snak.title} — ${snak.category} Snak thumbnail`} className="absolute block max-w-none" style={{
      width: `${stationSource.width / shelfThumb.width * 100}%`,
      left: `-${snak.x / shelfThumb.width * 100}%`,
      top: `-${snak.y / shelfThumb.height * 100}%`
    }} data-id="element-445" data-mp="3qclvxaaj8uq9" />
    </div>;
}